import { PC_KEYWORDS_EN_TOP, PC_KEYWORDS_EN_TOP_TOTAL } from "@/content/police-clearance-corpus/top.gen";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, CheckCircle2, MapPin, Shield, Sparkles, Clock } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { COMPANY } from "@/lib/company";
import {
  getPcProvinceLanding,
  PC_PROVINCE_LANDINGS,
  type PcProvinceLanding,
} from "@/content/police-clearance-provinces";
import type { PoliceClearanceCountry } from "@/content/police-clearance/registry";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";

import { buildPcProvinceLocalBusiness } from "@/content/police-clearance/province-gbp";
import { PcProvinceCrossLinks } from "@/components/PcProvinceCrossLinks";
import { FaqSourceNote } from "@/components/FaqSourceNote";

function buildProvinceFaqsEn(p: PcProvinceLanding, corpusFaqs: { q: string; a: string }[] = []) {
  return [
    {
      q: `Where can I get a Thai Police Clearance Certificate in ${p.provinceEn}?`,
      a: `Our team serves ${p.provinceEn} end-to-end — fingerprinting at ${p.rtpBranchTh} (${p.rtpBranchAddress}), or a home-visit appointment for FD-258 cards.`,
    },
    {
      q: `How long does it take from ${p.provinceEn}?`,
      a: p.turnaroundDaysTh,
    },
    {
      q: `Do I need to travel to Bangkok?`,
      a: `No — we handle fingerprinting, PCSC/FBI/ACRO/AFP submission, MFA legalization and Apostille from ${p.provinceEn} via Power of Attorney. Contact LINE ${COMPANY.lineId}.`,
    },
    {
      q: `What fingerprinting options are available in ${p.provinceEn}?`,
      a: p.fingerprintOptions.join(" · "),
    },
    {
      q: `Delivery from ${p.provinceEn}?`,
      a: p.courierEta,
    },
    ...corpusFaqs.slice(0, 6),
  ];
}

export const Route = createFileRoute("/en/police-clearance/province/$province")({
  loader: async ({ params }): Promise<{ p: NonNullable<ReturnType<typeof getPcProvinceLanding>>; PC_KEYWORDS_EN: string[]; PC_FAQ_EN: { q: string; a: string }[]; topCountries: PoliceClearanceCountry[] }> => {
    const p = getPcProvinceLanding(params.province);
    if (!p) throw notFound();
    const [m0, m1] = await Promise.all([
      import("@/content/police-clearance-corpus"),
      import("@/content/police-clearance/registry"),
    ]);
    return {
      p,
      PC_KEYWORDS_EN: m0.PC_KEYWORDS_EN,
      PC_FAQ_EN: m0.PC_FAQ_EN,
      topCountries: m1.POLICE_CLEARANCE_COUNTRIES.slice(0, 6),
    };
  },
  head: ({ params, loaderData }) => {
    const { PC_KEYWORDS_EN = [], PC_FAQ_EN = [] } = loaderData ?? {};

    const p = loaderData?.p ?? getPcProvinceLanding(params.province);
    if (!p) {
      return pageHeadForProvince({
        title: "Province not found",
        description: "No Police Clearance landing for this province.",
        path: `/en/police-clearance/province/${params.province}`,
      });
    }
    const site = getActiveSiteUrl();
    const title = `Thai Police Clearance in ${p.provinceEn} — FBI/ACRO/PCSC 2026`;
    const description = `Full-service Thai Police Clearance from ${p.provinceEn}: FD-258 fingerprinting, PCSC, FBI, ACRO, AFP, Apostille & certified translation — no Bangkok trip required.`;
    const base = pageHeadForProvince({
      title: title.length > 60 ? title.slice(0, 59) + "…" : title,
      description: description.length > 158 ? description.slice(0, 157) + "…" : description,
      path: `/en/police-clearance/province/${p.slug}`,
      type: "article",
      alternates: [{ lang: "th", path: `/police-clearance/province/${p.slug}` }],
      keywords: [
        `Police Clearance ${p.provinceEn}`,
        `PCC ${p.provinceEn}`,
        `FBI background check ${p.provinceEn}`,
        `ACRO ${p.provinceEn}`,
        `PCSC ${p.provinceEn}`,
        `Apostille ${p.provinceEn}`,
        `fingerprinting ${p.provinceEn}`,
        ...PC_KEYWORDS_EN_TOP.slice(0, 30),
      ],
    });
    const faqs = buildProvinceFaqsEn(p, PC_FAQ_EN);
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Police Clearance", url: `${site}/police-clearance` },
              { name: p.provinceEn, url: `${site}/en/police-clearance/province/${p.slug}` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": `${site}/en/police-clearance/province/${p.slug}#service`,
            name: `Police Clearance Certificate — ${p.provinceEn}`,
            serviceType: "Police Clearance Certificate Procurement",
            description: `Full-service Thai Police Clearance from ${p.provinceEn} — FD-258, PCSC, FBI, ACRO, AFP, MFA Apostille.`,
            provider: {
              "@type": "LegalService",
              name: COMPANY.legalName,
              telephone: COMPANY.phone,
              url: site,
              priceRange: "฿฿",
            },
            areaServed: { "@type": "AdministrativeArea", name: p.provinceEn },
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(buildPcProvinceLocalBusiness(p, site, "en")),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "#ai-answer-tldr"] },
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/en/police-clearance/province/${params.province}`)) },
      ],
    };
  },
  component: PcProvincePageEn,
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold">Province not found</h1>
        <Link to="/police-clearance" className="mt-4 inline-flex items-center gap-1 text-gold">
          <ArrowLeft className="h-4 w-4" /> Back to Police Clearance
        </Link>
      </div>
      <SiteFooter />
    </div>
  ),
});

function PcProvincePageEn() {
  const { PC_KEYWORDS_EN, PC_FAQ_EN, topCountries } = Route.useLoaderData() as { p: NonNullable<ReturnType<typeof getPcProvinceLanding>>; PC_KEYWORDS_EN: string[]; PC_FAQ_EN: { q: string; a: string }[]; topCountries: PoliceClearanceCountry[] };
  const { p } = Route.useLoaderData() as { p: PcProvinceLanding };
  const faqs = buildProvinceFaqsEn(p, PC_FAQ_EN);
  const otherProvinces = PC_PROVINCE_LANDINGS.filter((x) => x.slug !== p.slug);
  
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <nav className="border-b border-border bg-muted/30">
          <div className="mx-auto max-w-5xl px-4 py-3 text-xs text-muted-foreground">
            <Link to="/en" className="hover:text-primary">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/police-clearance" className="hover:text-primary">Police Clearance</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">{p.provinceEn}</span>
          </div>
        </nav>

        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <div className="text-xs font-bold uppercase tracking-widest text-gold">
              <MapPin className="mr-1 inline h-3.5 w-3.5" /> {p.region} · {p.provinceTh}
            </div>
            <h1 className="mt-2 text-3xl font-extrabold md:text-4xl">
              Thai Police Clearance in {p.provinceEn} — Full Service 2026
            </h1>
            <p id="ai-answer-tldr" className="mt-4 rounded-2xl border border-gold/30 bg-gold/5 p-5 text-base leading-relaxed">
              <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-gold">
                <Sparkles className="mr-1 inline h-3.5 w-3.5" /> AI Quick Answer
              </span>
              We deliver end-to-end Thai Police Clearance service from {p.provinceEn}: FD-258
              fingerprinting, PCSC/FBI/ACRO/AFP submission, MFA legalization and Apostille (28 February 2027)
              plus certified translation — no Bangkok trip required.
            </p>
            <div className="mt-4 rounded-lg border bg-muted/30 p-3 text-sm">
              <strong>ภาษาไทย:</strong>{" "}
              <Link
                to="/police-clearance/province/$province"
                params={{ province: p.slug }}
                className="text-primary underline"
              >
                อ่านหน้าภาษาไทย →
              </Link>
            </div>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto grid max-w-5xl gap-3 px-4 py-8 md:grid-cols-3">
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-semibold uppercase text-muted-foreground">Primary RTP branch</div>
              <div className="mt-1 text-sm font-bold">{p.rtpBranchTh}</div>
              <div className="mt-1 text-xs text-muted-foreground">{p.rtpBranchAddress}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-semibold uppercase text-muted-foreground">Fingerprint window</div>
              <div className="mt-1 text-sm font-bold"><Clock className="mr-1 inline h-4 w-4 text-gold" />{p.pickupWindow}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-semibold uppercase text-muted-foreground">Delivery</div>
              <div className="mt-1 text-sm font-bold">{p.courierEta}</div>
            </div>
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-2xl font-bold">Why our {p.provinceEn} service stands out</h2>
            <ul className="mt-5 grid gap-3 md:grid-cols-2">
              {p.localHighlights.map((h, i) => (
                <li key={i} className="flex items-start gap-2 rounded-xl border border-border bg-card p-4 text-sm">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-2xl font-bold">Fingerprinting options in {p.provinceEn}</h2>
            <ol className="mt-5 space-y-3">
              {p.fingerprintOptions.map((opt, i) => (
                <li key={i} className="flex gap-3 rounded-xl border border-border bg-card p-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold text-sm font-bold text-foreground">
                    {i + 1}
                  </div>
                  <div className="text-sm">{opt}</div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-2xl font-bold">Popular destination countries from {p.provinceEn}</h2>
            <div className="mt-5 grid gap-3 md:grid-cols-3">
              {topCountries.map((c) => (
                <Link
                  key={c.slug}
                  to="/police-clearance/$country"
                  params={{ country: c.slug }}
                  className="rounded-xl border border-border bg-card p-4 text-sm transition-all hover:-translate-y-0.5 hover:border-gold"
                >
                  <div className="text-xl">{c.flag}</div>
                  <div className="mt-1 font-bold text-primary">{c.authorityShort}</div>
                  <div className="text-xs text-muted-foreground">{c.certName}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-3xl px-4 py-12">
            <h2 className="text-2xl font-bold">FAQ — {p.provinceEn}</h2>
            <div className="mt-5 space-y-3">
              {faqs.map((f, i) => (
                <details key={i} className="group rounded-xl border border-border bg-card p-4">
                  <summary className="cursor-pointer font-semibold text-primary">{f.q}</summary>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <PcProvinceCrossLinks provinceLabel={p.provinceEn} lang="en" />

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-xl font-bold">Other provinces we serve</h2>
            <div className="mt-5 grid gap-3 md:grid-cols-4">
              {otherProvinces.map((op) => (
                <Link
                  key={op.slug}
                  to="/en/police-clearance/province/$province"
                  params={{ province: op.slug }}
                  className="rounded-xl border border-border bg-card p-4 text-sm transition-all hover:-translate-y-0.5 hover:border-gold"
                >
                  <MapPin className="h-4 w-4 text-gold" />
                  <div className="mt-1 font-bold text-primary">{op.provinceEn}</div>
                  <div className="text-xs text-muted-foreground">{op.region}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-gradient-to-b from-background to-primary/5">
          <div className="mx-auto max-w-3xl px-4 py-16 text-center">
            <Shield className="mx-auto h-10 w-10 text-gold" />
            <h2 className="mt-4 text-2xl font-bold">Ready to start from {p.provinceEn}?</h2>
            <p className="mt-3 text-sm text-muted-foreground">
              Free case review by our Notarial Services Attorneys — no deposit required.
            </p>
            <a
              href={`https://line.me/R/ti/p/${COMPANY.lineId}`}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold text-foreground shadow-lg hover:opacity-90"
            >
              Chat with our team
            </a>
          </div>
        </section>
        <div className="mx-auto max-w-4xl px-4">
          <FaqSourceNote topic="police-clearance" locale="en" />
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}